import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Eye, EyeOff, ShieldOff, ShieldCheck, UserCheck, Users2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import ImageUploader from "@/components/ImageUploader";

const VIS_LABEL = { public: "Active", hidden: "Hidden", suspended: "Suspended" };
const VIS_COLOR = { public: "text-[#00e676]", hidden: "text-[#ffea00]", suspended: "text-[#ff3333]" };

export default function AdminArtists() {
    const [q, setQ] = useState("");
    const [visibility, setVis] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [memberFor, setMemberFor] = useState(null); // artist row we're editing members on

    const load = () => {
        setLoading(true);
        endpoints.adminArtists({ q: q || undefined, visibility: visibility || undefined })
            .then((d) => setRows(d.artists))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, visibility]);
    useEffect(() => { endpoints.adminUsers().then((d) => setUsers(d.users)); }, []);

    const toggleHidden = async (a) => {
        const nextVis = a.visibility === "hidden" ? "public" : "hidden";
        if (!window.confirm(`${nextVis === "hidden" ? "Hide" : "Restore"} "${a.name}" ${nextVis === "hidden" ? "from public views" : "to public views"}?`)) return;
        try {
            await endpoints.adminUpdateArtist(a.id, { visibility: nextVis });
            toast.success(nextVis === "hidden" ? "Artist hidden" : "Artist restored");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleSuspend = async (a) => {
        const nextVis = a.visibility === "suspended" ? "public" : "suspended";
        const label = nextVis === "suspended" ? "SUSPEND" : "restore";
        if (!window.confirm(`${label} "${a.name}"? Analytics and history remain intact.`)) return;
        try {
            await endpoints.adminUpdateArtist(a.id, { visibility: nextVis });
            toast.success(label === "SUSPEND" ? "Artist suspended" : "Artist restored");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="pm-fade" data-testid="admin-artists">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Manage</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Artists</h1>
                </div>
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogTrigger asChild>
                        <button className="pm-btn pm-btn-primary" data-testid="admin-create-artist-btn"><Plus className="w-4 h-4 mr-2" /> Create artist</button>
                    </DialogTrigger>
                    <CreateArtistDialog users={users} onDone={() => { setCreateOpen(false); load(); }} />
                </Dialog>
            </header>

            <section className="px-4 md:px-8 py-4 flex flex-col md:flex-row gap-3 border-b border-[#2a2a2a] sticky top-0 bg-[#0a0a0a] z-10">
                <div className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 flex-1">
                    <Search className="w-4 h-4 text-[#666]" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, city, country, genre…" className="flex-1 bg-transparent outline-none text-sm" data-testid="admin-artist-search" />
                </div>
                <select value={visibility} onChange={(e) => setVis(e.target.value)} className="bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-xs" data-testid="admin-artist-visibility-filter">
                    <option value="" className="bg-[#141414]">All</option>
                    <option value="public" className="bg-[#141414]">Active</option>
                    <option value="hidden" className="bg-[#141414]">Hidden</option>
                    <option value="suspended" className="bg-[#141414]">Suspended</option>
                </select>
            </section>

            <div className="px-2 md:px-4">
                {loading ? (
                    <div className="p-8 font-mono text-xs text-[#666]">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-8 font-mono text-xs text-[#666]" data-testid="admin-artists-empty">No artists match.</div>
                ) : (
                    <div className="border border-[#2a2a2a] mt-3">
                        {rows.map((a) => (
                            <div key={a.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm" data-testid={`admin-artist-row-${a.id}`}>
                                <div className="col-span-12 md:col-span-4">
                                    <div className="font-display font-bold uppercase truncate">{a.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666] truncate">{a.genre} · {a.city}, {a.country}</div>
                                </div>
                                <div className="col-span-4 md:col-span-3 text-xs font-mono truncate" data-testid={`admin-artist-owner-${a.id}`}>
                                    {a.owner ? <><span className="text-[#a3a3a3]">Owner:</span> {a.owner.email}</> : <span className="text-[#ff4400]">no owner</span>}
                                </div>
                                <div className="col-span-4 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]">
                                    <span data-testid={`admin-artist-team-${a.id}`}>{a.team_size}</span> team · <span>{a.upcoming_event_count}</span> upcoming
                                </div>
                                <div className={`col-span-2 md:col-span-1 text-[10px] font-mono uppercase ${VIS_COLOR[a.visibility] || VIS_COLOR.public}`} data-testid={`admin-artist-vis-${a.id}`}>
                                    {VIS_LABEL[a.visibility] || "Active"}
                                </div>
                                <div className="col-span-12 md:col-span-2 flex items-center gap-1 justify-end flex-wrap">
                                    <button onClick={() => setMemberFor(a)} className="pm-btn text-[10px]" data-testid={`admin-artist-members-${a.id}`}>
                                        <Users2 className="w-3 h-3 mr-1" /> Members
                                    </button>
                                    <button onClick={() => toggleHidden(a)} className="pm-btn text-[10px]" data-testid={`admin-artist-hide-${a.id}`}>
                                        {a.visibility === "hidden" ? <><Eye className="w-3 h-3 mr-1" /> Show</> : <><EyeOff className="w-3 h-3 mr-1" /> Hide</>}
                                    </button>
                                    <button onClick={() => toggleSuspend(a)} className="pm-btn text-[10px]" data-testid={`admin-artist-suspend-${a.id}`}>
                                        {a.visibility === "suspended" ? <><ShieldCheck className="w-3 h-3 mr-1" /> Restore</> : <><ShieldOff className="w-3 h-3 mr-1" /> Suspend</>}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={!!memberFor} onOpenChange={(o) => !o && setMemberFor(null)}>
                {memberFor && <MembersDialog artist={memberFor} users={users} onDone={() => { setMemberFor(null); load(); }} />}
            </Dialog>
        </div>
    );
}

function CreateArtistDialog({ users, onDone }) {
    const [form, setForm] = useState({ name: "", genre: "", city: "", country: "", bio: "", image_url: "", cover_url: "", owner_user_id: "", region: "" });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!form.name.trim() || !form.genre.trim() || !form.city.trim() || !form.country.trim()) {
            toast.error("Name, genre, city, and country are required");
            return;
        }
        setBusy(true);
        try {
            await endpoints.adminCreateArtist({ ...form, owner_user_id: form.owner_user_id || undefined, region: form.region || undefined });
            toast.success("Artist created");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-lg">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Create artist</DialogTitle></DialogHeader>
            <div className="space-y-3">
                <TxtField label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} testId="artist-name-input" />
                <div className="grid grid-cols-2 gap-3">
                    <TxtField label="Genre" v={form.genre} on={(v) => setForm({ ...form, genre: v })} testId="artist-genre-input" />
                    <TxtField label="Region" v={form.region} on={(v) => setForm({ ...form, region: v })} placeholder="ethiopia | latin_america" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <TxtField label="City" v={form.city} on={(v) => setForm({ ...form, city: v })} testId="artist-city-input" />
                    <TxtField label="Country" v={form.country} on={(v) => setForm({ ...form, country: v })} testId="artist-country-input" />
                </div>
                <TxtField label="Bio" v={form.bio} on={(v) => setForm({ ...form, bio: v })} />
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3] mb-1">Profile image</div>
                        <ImageUploader
                            value={form.image_url}
                            onChange={(v) => setForm({ ...form, image_url: v })}
                            scope="admin"
                            aspect="square"
                            testId="admin-artist-image"
                        />
                    </div>
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3] mb-1">Cover image</div>
                        <ImageUploader
                            value={form.cover_url}
                            onChange={(v) => setForm({ ...form, cover_url: v })}
                            scope="admin"
                            aspect="video"
                            testId="admin-artist-cover"
                        />
                    </div>
                </div>
                <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Assign owner (optional)</span>
                    <select value={form.owner_user_id} onChange={(e) => setForm({ ...form, owner_user_id: e.target.value })} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="artist-owner-select">
                        <option value="" className="bg-[#141414]">— none —</option>
                        {users.map((u) => <option key={u.id} value={u.id} className="bg-[#141414]">{u.email}</option>)}
                    </select>
                </label>
            </div>
            <DialogFooter>
                <button onClick={submit} disabled={busy} className="pm-btn pm-btn-primary" data-testid="artist-create-submit">Create</button>
            </DialogFooter>
        </DialogContent>
    );
}

function MembersDialog({ artist, users, onDone }) {
    const [detail, setDetail] = useState(null);
    const [role, setRole] = useState("team");
    const [userId, setUserId] = useState("");
    const load = () => endpoints.adminGetArtist(artist.id).then(setDetail);
    useEffect(load, [artist.id]);
    const add = async () => {
        if (!userId) { toast.error("Pick a user"); return; }
        try { await endpoints.adminAddMember(artist.id, { user_id: userId, role }); toast.success("Member added"); load(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (uid) => {
        if (!window.confirm("Remove this member?")) return;
        try { await endpoints.adminRemoveMember(artist.id, uid); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-lg" data-testid="members-dialog">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Members · {artist.name}</DialogTitle></DialogHeader>
            <div className="space-y-2 max-h-64 overflow-auto border border-[#2a2a2a]">
                {(detail?.members || []).length === 0 && <div className="p-3 text-xs text-[#a3a3a3]">No members yet.</div>}
                {(detail?.members || []).map((m) => (
                    <div key={m.user_id} className="flex items-center gap-2 p-3 border-b border-[#2a2a2a] last:border-b-0" data-testid={`member-row-${m.user_id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-display font-bold uppercase text-sm truncate">{m.user?.name || m.user?.email}</div>
                            <div className="text-[10px] font-mono uppercase text-[#666] truncate">{m.user?.email} · role={m.role}</div>
                        </div>
                        <button onClick={() => remove(m.user_id)} className="pm-btn text-[10px]" data-testid={`member-remove-${m.user_id}`}>Remove</button>
                    </div>
                ))}
            </div>
            <div className="border border-[#2a2a2a] p-3 space-y-2">
                <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">Add member</div>
                <select value={userId} onChange={(e) => setUserId(e.target.value)} className="w-full bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="member-user-select">
                    <option value="" className="bg-[#141414]">Pick a user…</option>
                    {users.map((u) => <option key={u.id} value={u.id} className="bg-[#141414]">{u.email}</option>)}
                </select>
                <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="member-role-select">
                    <option value="owner" className="bg-[#141414]">Owner</option>
                    <option value="team" className="bg-[#141414]">Team Member</option>
                    <option value="staff" className="bg-[#141414]">Event Staff</option>
                </select>
                <button onClick={add} className="pm-btn pm-btn-primary w-full" data-testid="member-add-btn"><UserCheck className="w-4 h-4 mr-2" /> Add member</button>
            </div>
            <DialogFooter>
                <button onClick={onDone} className="pm-btn">Done</button>
            </DialogFooter>
        </DialogContent>
    );
}

function TxtField({ label, v, on, testId, placeholder }) {
    return (
        <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">{label}</span>
            <input placeholder={placeholder} value={v} onChange={(e) => on(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none" data-testid={testId} />
        </label>
    );
}
